/* eslint-disable */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import Collapse from '@mui/material/Collapse';
import { GRADIENT_TEXT_SX } from './PublicSeoPage';

// The one header for the whole site. Self-contained (MUI + react-router only, no
// dependency on the uncommitted marketing stylesheet that the legacy header.js
// needs), dark so it reads on every page, and a strict superset of the old nav:
// every destination the legacy header exposed is here, grouped the same way,
// plus Earn. Full dropdowns, a mobile hamburger drawer, Sign in, and a
// green-gradient Download button the theme cannot repaint. Used by PublicSeoPage
// and the download hub alike, so /news, /blog, /docs and /download share it.
//
// Dropdowns are pure CSS hover (no JS state) so they add nothing at prerender
// time and hydrate cleanly (no #418). Only the mobile drawer holds state, and it
// starts closed, so the first paint is deterministic. Every internal link is a
// react-router Link (SPA nav, works pre-hydration); external links are plain
// anchors that open in a new tab.
//
// NOTE: the "Events" link still points at etime.hertzai.com (a separate HertzAI
// product), kept here only to preserve the legacy nav until a hevolve.ai target
// is chosen. It is the last of the eTime leftovers.

const NAV = [
  { label: 'Home', to: '/' },
  {
    label: 'Products',
    children: [
      { label: 'Hevolve Agents', to: '/agents' },
      { label: 'Developer API', to: '/docs' },
      { label: 'Download Nunba', to: '/download' },
      { label: 'Earn Spark', to: '/earn' },
      { label: 'Pupit', to: '/pupit' },
    ],
  },
  {
    label: 'Developer Guide',
    children: [
      { label: 'Mindstory SDK', to: '/docs/sdk' },
      { label: 'Website widget', to: '/docs/widget' },
      { label: 'Hive API', to: '/docs/hive' },
      { label: 'Pupit video API', to: '/docs/client' },
      { label: 'Pupit SDK (server)', to: '/docs/server' },
      // The HART OS documentation site (GitHub Pages). It is live and has an
      // install/downloads section, but nothing in the site navigation pointed
      // at it -- the only link was in AgentSidebar, which no route renders, so
      // it was unreachable by browsing.
      { label: 'HART OS docs', href: 'https://docs.hevolve.ai', external: true },
    ],
  },
  { label: 'Try HevolveAI', to: '/agents' },
  {
    label: 'Learn',
    children: [
      { label: 'Blog', to: '/blog' },
      { label: 'News', to: '/news' },
      { label: 'Research explained', to: '/research' },
      { label: 'Answers', to: '/answers' },
      { label: 'Incident write-ups', to: '/incidents' },
      { label: 'Hive census', to: '/hive' },
      { label: 'Listings', to: '/listings' },
    ],
  },
  {
    label: 'Company',
    children: [
      { label: 'About us', to: '/aboutus' },
      { label: 'Press kit', to: '/press' },
      { label: 'Our Partners', to: '/partners' },
      { label: 'Events', href: 'https://etime.hertzai.com/event', external: true },
      { label: 'Contact', to: '/contact' },
      { label: 'Privacy', to: '/blog/privacy-first-ai' },
    ],
  },
  { label: 'Pricing', to: '/pricing' },
];

const navLinkSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  color: 'rgba(255,255,255,0.72)',
  textDecoration: 'none',
  fontSize: '0.95rem',
  fontWeight: 500,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  transition: 'color 0.2s',
  '&:hover': { color: '#fff' },
};

const submenuItemSx = {
  display: 'block',
  px: 2,
  py: 1,
  color: 'rgba(255,255,255,0.78)',
  textDecoration: 'none',
  fontSize: '0.9rem',
  whiteSpace: 'nowrap',
  transition: 'background 0.15s, color 0.15s',
  '&:hover': { background: 'rgba(124,114,255,0.16)', color: '#fff' },
};

// Renders a child link as a react-router Link (internal) or a plain anchor
// (external), sharing one style. onNav lets the mobile drawer close on click.
function SubLink({ item, sx, onNav }) {
  if (item.external) {
    return (
      <Box component="a" href={item.href} target="_blank" rel="noopener noreferrer" sx={sx} onClick={onNav}>
        {item.label}
      </Box>
    );
  }
  return (
    <Box component={Link} to={item.to} sx={sx} onClick={onNav}>
      {item.label}
    </Box>
  );
}

const DownloadButton = (props) => (
  <Button
    component={Link}
    to="/download"
    variant="contained"
    disableElevation
    sx={{
      // `background` (not bgcolor) so the theme's brand gradient cannot repaint it.
      background: 'linear-gradient(135deg, #17c07f, #10b981)',
      '&:hover': { background: 'linear-gradient(135deg, #14a86e, #0d9668)' },
      color: '#04140d',
      borderRadius: 999,
      textTransform: 'none',
      fontWeight: 700,
      px: 2.5,
      whiteSpace: 'nowrap',
      boxShadow: '0 6px 20px rgba(23,192,127,0.28)',
    }}
    {...props}
  >
    ⬇ Download Nunba
  </Button>
);

function MobileNav() {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const close = () => setOpen(false);
  return (
    <>
      <IconButton
        aria-label="Open menu"
        onClick={() => setOpen(true)}
        sx={{ display: { xs: 'inline-flex', md: 'none' }, color: '#fff' }}
      >
        {/* hamburger drawn in CSS so there is no icon-font dependency */}
        <Box sx={{ width: 22, height: 16, position: 'relative', '& span': { position: 'absolute', left: 0, width: '100%', height: 2, borderRadius: 2, background: '#fff' } }}>
          <Box component="span" sx={{ top: 0 }} />
          <Box component="span" sx={{ top: 7 }} />
          <Box component="span" sx={{ top: 14 }} />
        </Box>
      </IconButton>
      <Drawer
        anchor="right"
        open={open}
        onClose={close}
        PaperProps={{ sx: { width: 288, bgcolor: '#0F0E17', color: '#fff', borderLeft: '1px solid rgba(255,255,255,0.08)' } }}
      >
        <Box sx={{ p: 2 }}>
          <Box component={Link} to="/" onClick={close} sx={{ ...GRADIENT_TEXT_SX, fontSize: '1.25rem', textDecoration: 'none', display: 'inline-block', mb: 2 }}>
            Hevolve AI
          </Box>
          {NAV.map((n) =>
            n.children ? (
              <Box key={n.label}>
                <Box
                  onClick={() => setExpanded(expanded === n.label ? null : n.label)}
                  sx={{ ...navLinkSx, justifyContent: 'space-between', py: 1.25, width: '100%' }}
                >
                  {n.label}
                  <Box component="span" sx={{ transition: 'transform .2s', transform: expanded === n.label ? 'rotate(180deg)' : 'none' }}>▾</Box>
                </Box>
                <Collapse in={expanded === n.label}>
                  <Box sx={{ pl: 1.5, borderLeft: '1px solid rgba(255,255,255,0.1)', ml: 1 }}>
                    {n.children.map((c) => (
                      <SubLink key={c.label} item={c} sx={{ ...submenuItemSx, px: 1 }} onNav={close} />
                    ))}
                  </Box>
                </Collapse>
              </Box>
            ) : (
              <Box key={n.to} component={Link} to={n.to} onClick={close} sx={{ ...navLinkSx, display: 'flex', py: 1.25 }}>
                {n.label}
              </Box>
            )
          )}
          <Box component={Link} to="/signin" onClick={close} sx={{ ...navLinkSx, display: 'flex', py: 1.25 }}>
            Sign in
          </Box>
          <Box sx={{ mt: 2 }}>
            <DownloadButton fullWidth onClick={close} />
          </Box>
        </Box>
      </Drawer>
    </>
  );
}

export default function SeoHeader() {
  return (
    <Box
      component="header"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1100,
        bgcolor: 'rgba(11,10,18,0.85)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1.5 }}>
          {/* Wordmark */}
          <Box component={Link} to="/" sx={{ ...GRADIENT_TEXT_SX, fontSize: '1.35rem', textDecoration: 'none', whiteSpace: 'nowrap' }}>
            Hevolve AI
          </Box>

          {/* Desktop nav with CSS-hover dropdowns */}
          <Stack direction="row" spacing={2.5} sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
            {NAV.map((n) =>
              n.children ? (
                <Box key={n.label} sx={{ position: 'relative', '&:hover .submenu': { opacity: 1, visibility: 'visible', transform: 'translateY(0)' } }}>
                  <Box sx={navLinkSx}>
                    {n.label}
                    <Box component="span" sx={{ fontSize: '0.7rem', opacity: 0.7 }}>▾</Box>
                  </Box>
                  <Box
                    className="submenu"
                    sx={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      pt: 1,
                      opacity: 0,
                      visibility: 'hidden',
                      transform: 'translateY(6px)',
                      transition: 'all 0.18s ease',
                    }}
                  >
                    <Box sx={{ bgcolor: '#15131f', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2, py: 1, minWidth: 210, boxShadow: '0 16px 44px rgba(0,0,0,0.5)' }}>
                      {n.children.map((c) => (
                        <SubLink key={c.label} item={c} sx={submenuItemSx} />
                      ))}
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box component={Link} to={n.to} key={n.to} sx={navLinkSx}>
                  {n.label}
                </Box>
              )
            )}
          </Stack>

          {/* Right cluster: Sign in + Download (desktop), hamburger (mobile) */}
          <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
            <Box component={Link} to="/signin" sx={{ ...navLinkSx, display: { xs: 'none', md: 'inline-flex' } }}>
              Sign in
            </Box>
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <DownloadButton />
            </Box>
            <MobileNav />
          </Stack>
        </Box>
      </Container>
    </Box>
  );
}
